import { Stack, Typography } from '@mui/material';

import { ErrorState } from '@/components/common/ErrorState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { PageHeader } from '@/components/common/PageHeader';
import { ChangePasswordForm } from '@/features/identity/components/ChangePasswordForm';
import { useProfile } from '@/features/identity/hooks/useProfile';

/**
 * `/account/change-password`, serving both account shapes.
 *
 * <p>The credential shape comes from a fresh `/account/me` rather than the login-time session snapshot,
 * following `MfaSetupPage`, so an account that gains a password or a provider link mid-session sees the
 * right form on its next visit without signing out. The form waits for that read rather than defaulting
 * `hasPassword`, since either default shows the wrong screen to a real class of account.</p>
 */
export function ChangePasswordPage() {
  const { user, isLoading, error, refetch } = useProfile();

  return (
    <Stack spacing={3}>
      <PageHeader
        title={user && !user.hasPassword ? 'Set WillnQ password' : 'Change password'}
        description={
          user && !user.hasPassword
            ? 'Create a password so you can sign in without your social provider.'
            : 'Choose a strong, unique password for your account.'
        }
      />
      {isLoading ? <LoadingSpinner label="Loading your account…" /> : null}
      {!isLoading && error ? <ErrorState message={error} onRetry={refetch} /> : null}
      {!isLoading && !error && user ? (
        <ChangePasswordForm
          hasPassword={user.hasPassword}
          linkedProviders={user.linkedOAuthProviders}
        />
      ) : null}
      {!isLoading && !error && !user ? (
        <Typography color="text.secondary">Unable to load your account right now.</Typography>
      ) : null}
    </Stack>
  );
}
